import { fetchWithAuth } from "@/lib/api";
import type {
  AssetFundamentals,
  FinancialStatement,
  Dividend,
  Split,
  EarningsCalendar,
  Recommendation,
} from "@/schemas";
import type {
  ScreenerFilter,
  ScreenerFilterOptions,
  ScreenerFilterResult,
} from "@/schemas/screener.schema";

import { API_BASE } from "@/config/api";

export interface ScreenerPreferenceDTO {
  version?: number;
  column_order: string[];
  visibility: Record<string, boolean>;
  sort_by: string;
  sort_order: "asc" | "desc";
  active_filters: Record<string, unknown>;
  advanced_search: string;
  updated_at?: string;
}

const arrayQueryKeys = [
  "types",
  "sectors",
  "industries",
  "countries",
  "exchanges",
  "categories",
  "fund_families",
] as const;

const numberQueryKeys = [
  "min_market_cap",
  "max_market_cap",
  "min_enterprise_value",
  "max_enterprise_value",
  "min_dividend_yield",
  "max_dividend_yield",
  "min_pe",
  "max_pe",
  "min_forward_pe",
  "max_forward_pe",
  "min_peg_ratio",
  "max_peg_ratio",
  "min_roe",
  "max_roe",
  "min_roa",
  "max_roa",
  "min_price_to_book",
  "max_price_to_book",
  "min_ps",
  "max_ps",
  "min_pe_valuation",
  "max_pe_valuation",
  "min_pfcf",
  "max_pfcf",
  "min_revenue_growth",
  "max_revenue_growth",
  "min_earnings_growth",
  "max_earnings_growth",
  "min_gross_margins",
  "max_gross_margins",
  "min_operating_margins",
  "max_operating_margins",
  "min_profit_margins",
  "max_profit_margins",
  "min_current_ratio",
  "max_current_ratio",
  "min_quick_ratio",
  "max_quick_ratio",
  "min_payout_ratio",
  "max_payout_ratio",
  "min_beta",
  "max_beta",
  "min_short_ratio",
  "max_short_ratio",
  "min_average_volume",
  "max_average_volume",
  "min_float_shares",
  "max_float_shares",
  "min_shares_outstanding",
  "max_shares_outstanding",
  "min_held_percent_insiders",
  "max_held_percent_insiders",
  "min_held_percent_institutions",
  "max_held_percent_institutions",
  "min_latest_price",
  "max_latest_price",
  "min_daily_change_percent",
  "max_daily_change_percent",
  "min_fifty_two_week_high",
  "max_fifty_two_week_high",
  "min_fifty_two_week_low",
  "max_fifty_two_week_low",
  "min_fifty_day_average",
  "max_fifty_day_average",
  "min_two_hundred_day_average",
  "max_two_hundred_day_average",
  "min_expense_ratio",
  "max_expense_ratio",
  "min_yield",
  "max_yield",
  "min_ytd_return",
  "max_ytd_return",
  "min_three_year_avg_return",
  "max_three_year_avg_return",
  "min_five_year_avg_return",
  "max_five_year_avg_return",
  "min_beta_3year",
  "max_beta_3year",
  "min_net_assets",
  "max_net_assets",
  "limit",
  "offset",
] as const;

export class ScreenerService {
  /**
   * Filter assets with advanced criteria
   */
  static async filterAssets(
    filter: ScreenerFilter,
  ): Promise<ScreenerFilterResult> {
    const url = new URL(`${API_BASE}/assets/filter`);

    for (const key of arrayQueryKeys) {
      const value = filter[key];
      if (Array.isArray(value) && value.length > 0) {
        url.searchParams.append(key, value.join(","));
      }
    }
    for (const key of numberQueryKeys) {
      const value = filter[key];
      if (typeof value === "number") {
        url.searchParams.append(key, value.toString());
      }
    }
    if (filter.has_dividend !== undefined) {
      url.searchParams.append("has_dividend", filter.has_dividend.toString());
    }
    if (filter.sync !== undefined) {
      url.searchParams.append("sync", filter.sync.toString());
    }
    if (filter.sort_by) {
      url.searchParams.append("sort_by", filter.sort_by);
    }
    if (filter.sort_order) {
      url.searchParams.append("sort_order", filter.sort_order);
    }
    if (filter.search) {
      url.searchParams.append("search", filter.search);
    }
    if (filter.group_by) {
      url.searchParams.append("group_by", filter.group_by);
    }

    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to filter assets");
    }
    return response.json();
  }

  /**
   * Get available filter options (sectors, industries, etc.)
   */
  static async getFilterOptions(): Promise<ScreenerFilterOptions> {
    const response = await fetchWithAuth(`${API_BASE}/assets/filter-options`);
    if (!response.ok) {
      throw new Error("Failed to get filter options");
    }
    return response.json();
  }

  static async getScreenerPreference(
    scope: "stocks" | "etf",
  ): Promise<ScreenerPreferenceDTO | null> {
    const response = await fetchWithAuth(
      `${API_BASE}/users/me/ui-preferences/screener?scope=${scope}`,
    );
    if (!response.ok) {
      throw new Error("Failed to load screener preferences");
    }
    const data = await response.json();
    return data.preference ?? null;
  }

  static async putScreenerPreference(
    scope: "stocks" | "etf",
    body: Omit<ScreenerPreferenceDTO, "updated_at" | "version">,
  ): Promise<ScreenerPreferenceDTO | null> {
    const response = await fetchWithAuth(
      `${API_BASE}/users/me/ui-preferences/screener?scope=${scope}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to save screener preferences");
    }
    const data = await response.json();
    return data.preference ?? null;
  }

  /**
   * Get all fundamental data for an asset
   */
  static async getAssetFundamentals(
    assetId: string,
  ): Promise<AssetFundamentals> {
    const response = await fetchWithAuth(`${API_BASE}/financials/${assetId}`);
    if (!response.ok) {
      throw new Error("Failed to get asset fundamentals");
    }
    return response.json();
  }

  /**
   * Get financial statements for an asset
   */
  static async getFinancialStatements(
    assetId: string,
    statementType?: string,
    periodType?: string,
  ): Promise<{ statements: FinancialStatement[] }> {
    const url = new URL(`${API_BASE}/financials/${assetId}/statements`);
    if (statementType) {
      url.searchParams.append("type", statementType);
    }
    if (periodType) {
      url.searchParams.append("period", periodType);
    }
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to get financial statements");
    }
    return response.json();
  }

  /**
   * Get dividends for an asset
   */
  static async getDividends(
    assetId: string,
    limit?: number,
  ): Promise<{ dividends: Dividend[] }> {
    const url = new URL(`${API_BASE}/financials/${assetId}/dividends`);
    if (limit) {
      url.searchParams.append("limit", limit.toString());
    }
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to get dividends");
    }
    return response.json();
  }

  /**
   * Get stock splits for an asset
   */
  static async getSplits(assetId: string): Promise<{ splits: Split[] }> {
    const response = await fetchWithAuth(
      `${API_BASE}/financials/${assetId}/splits`,
    );
    if (!response.ok) {
      throw new Error("Failed to get splits");
    }
    return response.json();
  }

  /**
   * Get earnings calendar for an asset
   */
  static async getEarningsCalendar(assetId: string): Promise<EarningsCalendar> {
    const response = await fetchWithAuth(
      `${API_BASE}/financials/${assetId}/earnings`,
    );
    if (!response.ok) {
      throw new Error("Failed to get earnings calendar");
    }
    return response.json();
  }

  /**
   * Get analyst recommendations for an asset
   */
  static async getRecommendations(
    assetId: string,
    limit?: number,
  ): Promise<{ recommendations: Recommendation[] }> {
    const url = new URL(`${API_BASE}/financials/${assetId}/recommendations`);
    if (limit) {
      url.searchParams.append("limit", limit.toString());
    }
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to get recommendations");
    }
    return response.json();
  }

  /**
   * Get upcoming earnings across all assets
   */
  static async getUpcomingEarnings(
    limit?: number,
    days?: number,
  ): Promise<{ upcoming_earnings: EarningsCalendar[] }> {
    const url = new URL(`${API_BASE}/financials/earnings/upcoming`);
    if (limit) {
      url.searchParams.append("limit", limit.toString());
    }
    if (days) {
      url.searchParams.append("days", days.toString());
    }
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to get upcoming earnings");
    }
    return response.json();
  }

  /**
   * Get metrics series for financial charts
   */
  static async getMetricsSeries(
    assetId: string,
    period: "annual" | "quarterly" = "annual",
    metrics?: string[],
  ): Promise<MetricsSeriesResponse> {
    const url = new URL(`${API_BASE}/financials/${assetId}/series`);
    url.searchParams.append("period", period);
    if (metrics?.length) {
      url.searchParams.append("metrics", metrics.join(","));
    }
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to get metrics series");
    }
    return response.json();
  }

  /**
   * Get valuation ratio series (P/S, P/E, P/FCF) for an asset
   */
  static async getValuationSeries(
    assetId: string,
    options?: {
      periodType?: "annual" | "quarterly";
      granularity?: "daily";
      basis?: "ttm" | "annual";
      limit?: number;
    },
  ): Promise<ValuationSeriesResponse> {
    const url = new URL(`${API_BASE}/financials/${assetId}/valuation-series`);
    const periodType = options?.periodType ?? "annual";
    url.searchParams.append("period", periodType);
    if (options?.granularity) {
      url.searchParams.append("granularity", options.granularity);
    }
    if (options?.basis) {
      url.searchParams.append("basis", options.basis);
    }
    if (options?.limit !== undefined) {
      url.searchParams.append("limit", options.limit.toString());
    }

    const response = await fetchWithAuth(url.toString());
    if (!response.ok) {
      throw new Error("Failed to get valuation series");
    }
    return response.json();
  }
}

// Types for metrics series
export interface MetricsSeriesResponse {
  asset_id: string;
  period: string;
  series: Record<string, MetricSeries>;
}

export interface ValuationSeriesResponse {
  asset_id: string;
  period: string;
  series: Record<string, MetricSeries>;
  latest: {
    price_to_sales?: number;
    price_to_earnings?: number;
    price_to_fcf?: number;
  };
}

export interface MetricSeries {
  unit: string;
  currency?: string;
  data_points: MetricPoint[];
}

export interface MetricPoint {
  period_end_date: string;
  value: number;
}
